import { useContext, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import Table from 'react-bootstrap/Table';
import queryString from 'query-string';
import ConveraStatementsFiltersBar from './components/ConveraStatementsFiltersBar';
import ConveraStatementsTableHeadRow from './components/ConveraStatementsTableHeadRow';
import ConveraStatementsTableRowContent from './components/ConveraStatementsTableRowContent';
import { DIRECTION } from 'components/constants';
import EmptyTableContentPlug from 'components/EmptyTableContentPlug';
import Loader from 'components/Loader';
import i18nContext from 'components/i18n-context';
import Pagination from 'components/Pagination/Pagination';
import './ConveraStatementsTable.scss';

const ConveraStatementsTable = ({ converaStatementsStore }) => {
  const i18n = useContext(i18nContext);
  const location = useLocation();
  const navigate = useNavigate();

  const updateFiltersInUrl = (filters, pagination, sortDirection) => {
    const filtersParams = converaStatementsStore.prepareFiltersParams();
    const queryParams = {
      ...filtersParams,
      page: pagination.page,
      size: pagination.size,
      sort_direction: sortDirection
    };
    const searchParams = queryString.stringify(queryParams);
    navigate({ search: `?${searchParams}` }, { replace: true });
  };

  const loadFiltersFromUrl = () => {
    const params = queryString.parse(location.search);
    converaStatementsStore.setFiltersFromUrl(params);
  };

  useEffect(() => {
    loadFiltersFromUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!converaStatementsStore.isInitialized) {
      converaStatementsStore.getConveraStatementsList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [converaStatementsStore.isInitialized]);

  useEffect(() => {
    if (converaStatementsStore.isInitialized) {
      converaStatementsStore.resetConveraStatementsStore();
      loadFiltersFromUrl();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    updateFiltersInUrl(
      converaStatementsStore.filters,
      converaStatementsStore.pagination,
      converaStatementsStore.sortDirection
    );
    if (converaStatementsStore.isInitialized) {
      converaStatementsStore.getConveraStatementsList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    converaStatementsStore.filters.createdFrom,
    converaStatementsStore.filters.createdTo,
    converaStatementsStore.filters.type,
    converaStatementsStore.filters.filename,
    converaStatementsStore.pagination.size,
    converaStatementsStore.pagination.page,
    converaStatementsStore.sortDirection
  ]);

  const handleOnSortTable = () => {
    converaStatementsStore.setSortDirection(
      converaStatementsStore.sortDirection === DIRECTION.ASC ? DIRECTION.DESC : DIRECTION.ASC
    );
  };

  if (!converaStatementsStore.isInitialized) {
    return <Loader className={'application-loader'} />;
  }

  return (
    <div className={'convera-statements-wrapper'}>
      <ConveraStatementsFiltersBar
        filters={converaStatementsStore.filters}
        totalElements={converaStatementsStore.totalElements}
        handleSetFilters={converaStatementsStore.setFilter}
      />

      {converaStatementsStore.error ? (
        <EmptyTableContentPlug
          headerText={i18n.getMessage('converaStatements.table.errorPlug.header')}
          descriptionText={i18n.getMessage('converaStatements.table.errorPlug.description')}
        />
      ) : converaStatementsStore.converaStatementsList?.length === 0 ? (
        <EmptyTableContentPlug
          headerText={i18n.getMessage('converaStatements.table.emptyPlug.header')}
          descriptionText={i18n.getMessage('converaStatements.table.emptyPlug.description')}
        />
      ) : (
        <>
          <Pagination
            isShowPaginationSize={true}
            paginationSize={converaStatementsStore.pagination.size}
            handleChangePaginationSize={converaStatementsStore.setPageSize}
            isLoading={converaStatementsStore.isLoading}
            currentPage={converaStatementsStore.pagination.page}
            numberOfPages={converaStatementsStore.pagination.totalPages}
            setCurrentPage={converaStatementsStore.setPageNumber}
          />

          <Table responsive>
            <thead>
              <ConveraStatementsTableHeadRow
                handleOnSortTable={handleOnSortTable}
                direction={converaStatementsStore.sortDirection}
              />
            </thead>
            <tbody>
              {converaStatementsStore.converaStatementsList?.map((file, index) => (
                <ConveraStatementsTableRowContent
                  key={file.id || index}
                  isLoading={converaStatementsStore.isLoading}
                  fileData={file}
                  handleDownloadFileById={converaStatementsStore.downloadConveraStatementById}
                />
              ))}
            </tbody>
          </Table>

          <Pagination
            isShowPaginationSize={true}
            paginationSize={converaStatementsStore.pagination.size}
            handleChangePaginationSize={converaStatementsStore.setPageSize}
            isLoading={converaStatementsStore.isLoading}
            currentPage={converaStatementsStore.pagination.page}
            numberOfPages={converaStatementsStore.pagination.totalPages}
            setCurrentPage={converaStatementsStore.setPageNumber}
          />
        </>
      )}
    </div>
  );
};

ConveraStatementsTable.propTypes = {
  converaStatementsStore: MobXPropTypes.observableObject
};

export default inject((stores) => ({
  converaStatementsStore: stores.converaStatementsStore
}))(observer(ConveraStatementsTable));
